/**
 * Sprint T-18 · Notification Studio · Playground · History list.
 *
 * Global across superadmins (Q7 = b). Click on a row → restores params.
 * Reads via `usePlaygroundHistory` (60s stale).
 */
import React from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { usePlaygroundHistory } from "../../../hooks/useNotifications";

function short(id) {
  return id ? `${id.slice(0, 6)}…` : "—";
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const s = Math.max(1, Math.round((Date.now() - d) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

function OutcomePill({ outcome }) {
  const cls =
    outcome === "sent"
      ? "bg-emerald-500/20 text-emerald-300"
      : outcome === "invalid_template" || outcome === "failed"
      ? "bg-rose-500/20 text-rose-300"
      : "bg-amber-500/20 text-amber-300";
  return (
    <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] ${cls}`}>
      {outcome || "—"}
    </span>
  );
}

export default function PlaygroundHistoryList({ onRestore, limit = 20 }) {
  const { data, isLoading, error, refetch, isFetching } = usePlaygroundHistory({ limit });
  const items = data?.items || [];

  return (
    <div
      className="flex flex-col gap-2 rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-3"
      data-testid="studio-playground-history"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
          Playground history · {data?.total ?? 0} entries
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          data-testid="studio-playground-history-refresh"
          className="rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/50 hover:text-[var(--ag-text)]"
        >
          {isFetching ? <Loader2 size={10} className="animate-spin" /> : "↻"}
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-[var(--ag-text-dim)]">
          <Loader2 size={12} className="animate-spin" />
          Cargando historial…
        </div>
      )}
      {error && (
        <div className="text-xs text-rose-300">Error cargando historial</div>
      )}
      {!isLoading && items.length === 0 && (
        <div
          className="rounded border border-dashed border-[var(--ag-border)] px-3 py-4 text-center text-xs text-[var(--ag-text-dim)]"
          data-testid="studio-playground-history-empty"
        >
          Sin pruebas todavía · dispara un Dry Run para empezar.
        </div>
      )}

      {items.length > 0 && (
        <div className="max-h-[280px] overflow-y-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="text-[9px] uppercase tracking-widest text-[var(--ag-text-dim)]">
              <tr>
                <th className="px-1 py-1">age</th>
                <th className="px-1 py-1">user</th>
                <th className="px-1 py-1">template</th>
                <th className="px-1 py-1">action</th>
                <th className="px-1 py-1">outcome</th>
                <th className="px-1 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-[var(--ag-border)]/40"
                  data-testid={`studio-playground-history-row-${short(it.id)}`}
                >
                  <td className="px-1 py-1 font-mono text-[10px] text-[var(--ag-text-dim)]">
                    {timeAgo(it.created_at)}
                  </td>
                  <td className="px-1 py-1 text-[10px] text-[var(--ag-text-dim)]">
                    {it.user_email?.slice(0, 12) || "—"}
                  </td>
                  <td className="px-1 py-1 font-mono text-[10px] text-[var(--ag-text)]">
                    {(it.template_id || "").replace("email.", "")}
                  </td>
                  <td className="px-1 py-1 font-mono text-[9px]">
                    <span className="rounded bg-[var(--ag-panel)] px-1 py-0.5 text-[var(--ag-text-dim)]">
                      {it.action}
                    </span>
                  </td>
                  <td className="px-1 py-1">
                    <OutcomePill outcome={it.outcome} />
                  </td>
                  <td className="px-1 py-1 text-right">
                    <button
                      type="button"
                      onClick={() => onRestore?.(it)}
                      title="Restaurar en el editor"
                      data-testid={`studio-playground-history-restore-${short(it.id)}`}
                      className="rounded border border-[var(--ag-border)] p-1 text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/60 hover:text-[var(--ag-text)]"
                    >
                      <RotateCcw size={10} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
